import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'

import { DisplayScreen, MultiScreen, RootStateTypeExtra } from '../types'

const STORAGE_KEY = 'multiScreen'

const DEFAULT_STATE: MultiScreen = {
  active: false,
  displayScreens: [],
  mirroredScreens: [],
}

const getMultiScreenInitialState = (): MultiScreen => {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY)
    return storedValue ? JSON.parse(storedValue) : DEFAULT_STATE
  } catch {
    return DEFAULT_STATE
  }
}

const saveToStorage = (state: MultiScreen) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

const initialState: MultiScreen = getMultiScreenInitialState()

const multiScreenSlice = createSlice({
  name: 'multiScreen',
  initialState,
  reducers: {
    activateMultiScreen: (state, action: PayloadAction<boolean>) => {
      const nextState: MultiScreen = {
        ...state,
        active: action.payload,
      }

      saveToStorage(nextState)
      return nextState
    },

    setDisplayScreens: (state, action: PayloadAction<DisplayScreen[]>) => {
      const nextState: MultiScreen = {
        ...state,
        displayScreens: action.payload,
      }

      saveToStorage(nextState)
      return nextState
    },

    updateDisplayScreenProject: (
      state,
      action: PayloadAction<{ masterKey: string; projectName: string }>
    ) => {
      const updatedScreens = state.displayScreens.map((screen) =>
        screen.masterKey === action.payload.masterKey
          ? { ...screen, currentProject: action.payload.projectName }
          : screen
      )

      const nextState: MultiScreen = {
        ...state,
        displayScreens: updatedScreens,
      }

      saveToStorage(nextState)
      return nextState
    },

    addMirroredScreen: (state, action: PayloadAction<string>) => {
      const masterKey = action.payload

      const nextState: MultiScreen = {
        ...state,
        mirroredScreens: [...state.mirroredScreens, masterKey],
      }

      saveToStorage(nextState)
      return nextState
    },

    removeMirroredScreen: (state, action: PayloadAction<string>) => {
      const masterKey = action.payload

      const nextState: MultiScreen = {
        ...state,
        mirroredScreens: state.mirroredScreens.filter(
          (key) => key !== masterKey
        ),
      }

      saveToStorage(nextState)
      return nextState
    },

    resetMultiScreen: () => {
      localStorage.removeItem(STORAGE_KEY)
      return DEFAULT_STATE
    },
  },
})

export const selectMultiScreen = (state: RootStateTypeExtra) =>
  state.multiScreen

export const selectDisplayScreens = (state: RootStateTypeExtra) =>
  state.multiScreen.displayScreens

export const selectMirroredScreens = (state: RootStateTypeExtra) =>
  state.multiScreen.mirroredScreens

export const selectIsShowcaseMultiScreen = createSelector(
  [
    (state) => state.projectIdentity.masterKey,
    (state) => state.multiScreen.displayScreens,
  ],
  (masterKey, multiScreenDisplays) =>
    Boolean(masterKey && multiScreenDisplays.length > 0)
)

export const {
  activateMultiScreen,
  setDisplayScreens,
  updateDisplayScreenProject,
  addMirroredScreen,
  removeMirroredScreen,
  resetMultiScreen,
} = multiScreenSlice.actions

export default multiScreenSlice.reducer
